import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { AlertTriangle, ShieldAlert, Loader2, ArrowLeft, X as CloseIcon } from "lucide-react";
import { toast } from "sonner";
import yLogoDark from "@/assets/brand/y-logo-dark.png";
import api, { apiError as fmtApiError, tokenStore } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/lib/useSeo";

/**
 * HelpDeleteConfirm — post-login destructive-action gate.
 *
 * Only reachable when the user is authenticated (any redirect from the
 * /help/delete auth flow lands here via the sessionStorage post-login
 * override read by AuthContext.commitLoginWithWelcome).
 *
 * Two-step confirmation modal — matches the destructive UX from
 * app/Settings.jsx.  On the final confirmation we call the SAME
 * endpoint used by Settings (`EP.deleteAccount` → /account-delete/delete-account)
 * with the same payload shape.
 *
 * Success  → /help/delete/done      (Y-logo confirmation screen)
 * Error    → in-content banner with the raw Xano message
 */
export default function HelpDeleteConfirm() {
  const navigate = useNavigate();
  const { user, authed, loading } = useAuth();

  const [step, setStep] = useState(0);       // 0 = closed, 1 = first prompt, 2 = final
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Clear the post-login redirect flag as soon as we're here — the
  // caller shouldn't be able to loop back to this page after a normal
  // sign-out/sign-in.
  useEffect(() => {
    try {
      sessionStorage.removeItem("brontoy_post_login_redirect");
    } catch {
      /* storage disabled */
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-dvh bg-black text-white/60 text-sm flex items-center justify-center">
        Loading…
      </div>
    );
  }
  // Not signed in — bounce back to the auth-options entry point.
  if (!authed) return <Navigate to="/help/delete" replace />;

  const doDelete = async () => {
    if (busy) return;
    if (step === 1) {
      setStep(2);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.post(EP.deleteAccount, {
        user_id: Number(user?.id),
        username: user?.username,
      });
      // Wipe auth tokens locally, then hard-navigate to the success page.
      // The hard reload guarantees AuthContext resets (`authed=false`, `user=null`)
      // so no in-app route can be reached afterwards.  We keep a marker in
      // sessionStorage so the success page knows to render its confirmation.
      try {
        tokenStore.clearAll();
        sessionStorage.setItem("brontoy_delete_done", "1");
      } catch {
        /* storage disabled */
      }
      window.location.replace("/help/delete/done");
    } catch (e) {
      setErr(fmtApiError(e) || "Couldn't delete your account. Please try again.");
      toast.error("Deletion failed. See details on screen.");
      setBusy(false);
      setStep(0);
    }
  };

  return (
    <div
      className="min-h-dvh w-full bg-black text-white flex flex-col relative overflow-hidden"
      data-testid="help-delete-confirm-screen"
      style={{
        fontFamily:
          "'Plus Jakarta Sans','TwitterChirp','Segoe UI',Helvetica,Arial,sans-serif",
      }}
    >
      <Seo
        title="Confirm Account Deletion — BrontoY"
        description="Confirm permanent deletion of your BrontoY account."
        canonicalPath="/help/delete-account"
      />

      {/* Red ambient glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          style={{
            position: "absolute",
            top: "-25%",
            left: "-10%",
            width: "700px",
            height: "700px",
            background:
              "radial-gradient(closest-side, rgba(244,63,94,0.28), transparent)",
            filter: "blur(50px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-30%",
            right: "-15%",
            width: "650px",
            height: "650px",
            background:
              "radial-gradient(closest-side, rgba(190,18,60,0.20), transparent)",
            filter: "blur(50px)",
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 h-14 px-4 md:px-8 flex items-center justify-between border-b border-white/10">
        <button
          type="button"
          onClick={() => navigate("/help")}
          className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[13.5px] font-medium"
          data-testid="help-delete-confirm-back"
        >
          <ArrowLeft size={16} strokeWidth={2.4} /> Back
        </button>
        <img src={yLogoDark} alt="BrontoY" className="h-7 w-auto" draggable={false} />
        <span className="w-10" />
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[560px] flex flex-col">
          {/* Central Y logo */}
          <div className="flex flex-col items-center mb-8">
            <img
              src={yLogoDark}
              alt="BrontoY"
              className="h-24 md:h-32 w-auto drop-shadow-[0_0_60px_rgba(244,63,94,0.35)]"
              draggable={false}
              data-testid="help-delete-y-logo"
            />
          </div>

          {/* Warning card */}
          <div className="rounded-3xl border border-red-500/30 bg-gradient-to-br from-red-950/50 via-black to-black p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: "linear-gradient(135deg, rgba(244,63,94,0.25), rgba(190,18,60,0.15))",
                  border: "1px solid rgba(244,63,94,0.4)",
                }}
              >
                <ShieldAlert size={22} className="text-red-400" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <h1
                  className="text-white text-[22px] md:text-[26px] font-extrabold tracking-tight leading-tight"
                  data-testid="help-delete-confirm-title"
                >
                  This will delete your account
                </h1>
                <p className="text-white/50 text-[12.5px] md:text-[13px] mt-0.5">
                  Signed in as{" "}
                  <span className="text-white/80 font-semibold">
                    @{user?.username || "you"}
                  </span>
                </p>
              </div>
            </div>

            <p className="text-white/75 text-[14.5px] md:text-[15px] leading-relaxed mb-4">
              Please read carefully before continuing. This action is{" "}
              <span className="text-red-300 font-semibold">permanent</span> and{" "}
              <span className="text-red-300 font-semibold">cannot be undone</span>.
            </p>

            <ul className="space-y-2 text-white/70 text-[13.5px] md:text-[14px] leading-relaxed">
              <BulletItem>Your profile, username, and display name will be released.</BulletItem>
              <BulletItem>All your posts, comments, and opinions will be permanently removed.</BulletItem>
              <BulletItem>Your followers, following list, and saved posts will be erased.</BulletItem>
              <BulletItem>You will be signed out on every device and cannot recover this account.</BulletItem>
              <BulletItem>If you signed up via Apple or Google, this does not remove BrontoY from your provider settings.</BulletItem>
            </ul>

            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-300" />
              <p className="text-white/70 text-[13px] leading-[19px]">
                We would gently ask you to reconsider — but if this is what you truly need,
                please continue below. The BrontoY team wishes you well.
              </p>
            </div>

            {err && (
              <div
                role="alert"
                className="mt-5 flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 text-red-100 px-4 py-3"
                data-testid="help-delete-error"
              >
                <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-300" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-red-200 mb-0.5">
                    Deletion failed
                  </div>
                  <div className="text-[12.5px] leading-[17px] break-words">
                    {err}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setErr(null)}
                  className="shrink-0 rounded-full p-1 text-red-200 hover:bg-red-500/20"
                  aria-label="Dismiss error"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => setStep(1)}
              disabled={busy}
              className="mt-6 w-full h-[52px] rounded-full font-bold text-[15px] flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110"
              style={{
                background: "linear-gradient(135deg, #ef4444, #b91c1c)",
                color: "white",
                boxShadow: "0 12px 40px -12px rgba(239,68,68,0.7)",
              }}
              data-testid="help-delete-open-modal"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Working…
                </>
              ) : (
                "Delete My Account"
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate("/")}
              disabled={busy}
              className="mt-2.5 w-full h-[46px] text-white/70 hover:text-white font-semibold text-[14px] disabled:opacity-50"
              data-testid="help-delete-keep"
            >
              Keep my account
            </button>
          </div>
        </div>
      </main>

      {/* Are-you-sure modal (2-step) */}
      {step > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center px-5"
          data-testid="help-delete-modal"
        >
          <div className="w-full max-w-[420px] rounded-3xl border border-red-500/25 bg-gradient-to-b from-[#191013] to-[#0b0507] p-6 md:p-7 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4"
              style={{ backgroundColor: "rgba(239,68,68,0.15)" }}
            >
              <AlertTriangle size={26} className="text-red-400" strokeWidth={2.2} />
            </div>
            <h3
              className="text-white text-[19px] font-bold text-center"
              data-testid="help-delete-modal-title"
            >
              {step === 1 ? "Delete account?" : "Are you absolutely sure?"}
            </h3>
            <p className="text-white/65 text-[13.5px] text-center mt-2 leading-5">
              {step === 1
                ? "This action cannot be undone. Your account and all of its data will be permanently removed from BrontoY."
                : `This will permanently erase @${user?.username || "your account"} — posts, followers, messages, everything. There is no way back.`}
            </p>

            <button
              type="button"
              onClick={doDelete}
              disabled={busy}
              className="mt-5 w-full h-[52px] rounded-full flex items-center justify-center text-white font-bold text-[15px] transition-all disabled:opacity-70 hover:brightness-110"
              style={{
                background: "linear-gradient(135deg, #ef4444, #b91c1c)",
                boxShadow: "0 10px 30px -10px rgba(239,68,68,0.6)",
              }}
              data-testid={
                step === 1 ? "help-delete-confirm-btn" : "help-delete-confirm-final-btn"
              }
            >
              {busy ? (
                <>
                  <Loader2
                    className="w-4 h-4 animate-spin"
                    data-testid="help-delete-loading"
                  />
                  <span className="ml-2">Deleting…</span>
                </>
              ) : step === 1 ? (
                "Delete"
              ) : (
                "Yes, delete my account"
              )}
            </button>
            <button
              type="button"
              onClick={() => (busy ? null : setStep(0))}
              disabled={busy}
              className="mt-2 w-full h-[48px] text-white/70 hover:text-white font-semibold text-[14px] disabled:opacity-50"
              data-testid="help-delete-modal-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BulletItem({ children }) {
  return (
    <li className="flex gap-2.5">
      <span
        className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-red-400"
        aria-hidden
      />
      <span>{children}</span>
    </li>
  );
}
